import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

export interface QuestionGroupNode {
  questions: (string | { key: string; name: string })[];
  subgroups?: {
    [subgroupName: string]: QuestionGroupNode;
  };
}

export interface QuestionGroup {
  [groupName: string]: QuestionGroupNode;
}

export interface QuestionMapping {
  id: number;
  project_id: number;
  jotform_form_id: string;
  form_mapping_id: number | null;
  mapping_config: {
    groups: QuestionGroup;
  };
  created_at: string;
  updated_at: string;
}

export interface FormQuestion {
  id: string;
  name: string;
  text: string;
  type: string;
  order: string;
}

/**
 * Hook to manage question mappings for a specific form
 */
export function useQuestionMappings(formId?: string) {
  const { projectId } = useProject();
  const queryClient = useQueryClient();

  // Fetch existing mapping for the form
  const { data: mapping, isLoading } = useQuery({
    queryKey: ['question-mappings', projectId, formId],
    queryFn: async () => {
      if (!formId || !projectId) return null;

      const { data, error } = await supabase
        .from('jotform_question_mappings')
        .select('*')
        .eq('project_id', projectId)
        .eq('jotform_form_id', formId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching question mapping:', error);
        throw error;
      }

      if (!data) return null;

      // Parse the mapping_config from Json to our typed structure
      return {
        ...data,
        mapping_config: data.mapping_config as unknown as { groups: QuestionGroup }
      } as QuestionMapping;
    },
    enabled: !!formId && !!projectId,
  });

  // Fetch all questions from a form
  const { data: formQuestions, isLoading: isLoadingQuestions, error: questionsError } = useQuery({
    queryKey: ['form-questions', formId],
    queryFn: async () => {
      if (!formId) return [];

      const JOTFORM_API_KEY = 'ab0570e246def5ab17e4a6ad141fe48d';
      const JOTFORM_API_URL = 'https://eu-api.jotform.com';

      console.log(`Fetching questions for form: ${formId}`);

      // Get one submission to extract questions structure
      const response = await fetch(
        `${JOTFORM_API_URL}/form/${formId}/submissions?apiKey=${JOTFORM_API_KEY}&limit=1&orderby=created_at`
      );

      if (!response.ok) {
        console.error('JotForm API error:', response.status, response.statusText);
        throw new Error(`Error ${response.status}: No se pudieron obtener las preguntas del formulario.`);
      }

      const data = await response.json();
      console.log('JotForm API response:', data);

      const submissions = data.content || [];

      if (submissions.length === 0) {
        throw new Error('No hay submissions en este formulario. Crea al menos una submission para poder organizar las preguntas.');
      }

      // Extract questions from the first submission
      const firstSubmission = submissions[0];
      console.log('First submission:', firstSubmission);

      const answers = firstSubmission.answers || {};
      console.log('Answers object:', answers);
      console.log('Number of answers:', Object.keys(answers).length);

      const questions: FormQuestion[] = Object.entries(answers)
        .map(([id, answer]: [string, any]) => {
          console.log(`Processing question ${id}:`, answer);
          return {
            id,
            name: answer.name || id,
            text: answer.text || answer.name || id,
            type: answer.type || 'unknown',
            order: answer.order || '0'
          };
        })
        .filter(q => {
          // Filter out control elements that shouldn't be grouped
          const ignoreTypes = ['control_head', 'control_text', 'control_image', 'control_pagebreak', 'control_divider', 'control_button'];
          const shouldInclude = q.name && q.name.trim() !== '' && !ignoreTypes.includes(q.type);
          if (!shouldInclude) {
            console.log(`Filtering out question:`, q);
          }
          return shouldInclude;
        })
        .sort((a, b) => parseInt(a.order || '0') - parseInt(b.order || '0'));

      console.log(`Total questions after filtering: ${questions.length}`, questions);

      return questions;
    },
    enabled: !!formId,
    retry: 1,
  });

  // Create or update mapping
  const saveMutation = useMutation({
    mutationFn: async (mappingConfig: { groups: QuestionGroup }) => {
      if (!formId || !projectId) {
        throw new Error('Form ID and Project ID are required');
      }

      // Crear mapeo de nombres a números de JotForm
      const questionNumberMap: Record<string, string> = {};
      formQuestions.forEach(question => {
        questionNumberMap[question.name] = question.id;
      });

      // Función para convertir questions de strings a objetos
      const convertQuestionsToObjects = (groups: QuestionGroup): any => {
        const result: any = {};
        
        for (const [groupName, groupData] of Object.entries(groups)) {
          result[groupName] = {
            questions: groupData.questions.map(questionName => {
              const name = typeof questionName === 'string' ? questionName : questionName.name;
              return {
                name: name,
                key: questionNumberMap[name] || name
              };
            }),
            subgroups: groupData.subgroups ? convertQuestionsToObjects(groupData.subgroups) : {}
          };
        }
        
        return result;
      };

      const payload = {
        project_id: projectId,
        jotform_form_id: formId,
        mapping_config: {
          groups: convertQuestionsToObjects(mappingConfig.groups),
          questionNumberMap
        } as unknown as Json,
      };

      const { data, error } = await supabase
        .from('jotform_question_mappings')
        .upsert(payload, {
          onConflict: 'project_id, jotform_form_id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-mappings', projectId, formId] });
      toast.success('Configuración guardada exitosamente');
    },
    onError: (error) => {
      console.error('Error saving mapping:', error);
      toast.error('Error al guardar la configuración');
    }
  });

  // Delete mapping
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!formId || !projectId) {
        throw new Error('Form ID and Project ID are required');
      }

      const { error } = await supabase
        .from('jotform_question_mappings')
        .delete()
        .eq('project_id', projectId)
        .eq('jotform_form_id', formId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-mappings', projectId, formId] });
      toast.success('Configuración eliminada');
    },
    onError: (error) => {
      console.error('Error deleting mapping:', error);
      toast.error('Error al eliminar la configuración');
    }
  });

  return {
    mapping,
    formQuestions: formQuestions || [],
    isLoading: isLoading || isLoadingQuestions,
    saveMapping: saveMutation.mutate,
    deleteMapping: deleteMutation.mutate,
    isSaving: saveMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
